/**
 * ColumnBars — the mock's `.ichart`, i.e. a short ORDERED scale drawn as vertical
 * columns with a chip under each one (the referral L1→L5 intention ladder).
 *
 * Vertical, not horizontal, for one reason: the categories are a SEQUENCE, and a
 * left-to-right axis is the only reading in which "générique → spécifique" is
 * visible. Horizontal bars would sort it by value and destroy the ladder.
 *
 * Two rules it exists to keep:
 *  - **An unmeasured rung is hatched, not short.** `value: null` draws a diagonal
 *    hatch over the FULL column height plus « n. m. » — a 0-height bar next to a
 *    measured one reads as "we looked and found nothing".
 *  - **The scale caption is part of the chart.** `scaleFrom`/`scaleTo` render the
 *    gradient rule under the columns; without it a reader has no way to know that
 *    L1 and L5 are not just two names.
 */

import { Box, Typography } from '@mui/material';
import { VIZ } from './tokens';

export interface ColumnBar {
  /** The chip under the column: `L1`, `L2`… Mono, so the rungs align. */
  chip: string;
  value: number | null;
  /** Under the chip: « 25 réponses ». */
  sub?: string;
}

export interface ColumnBarsProps {
  columns: ColumnBar[];
  maxValue?: number;
  color?: string;
  unmeasuredNote?: string;
  /** Left and right ends of the ordered-scale caption. Both or neither. */
  scaleFrom?: string;
  scaleTo?: string;
  emptyNote?: string;
}

const PLOT_H = 130;

export default function ColumnBars({
  columns,
  maxValue = 100,
  color = VIZ.good,
  unmeasuredNote = 'n. m.',
  scaleFrom,
  scaleTo,
  emptyNote,
}: ColumnBarsProps) {
  if (columns.length === 0) {
    return emptyNote ? (
      <Typography sx={{ color: VIZ.muted, fontSize: 13.5, py: 1.5 }}>{emptyNote}</Typography>
    ) : null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
          gap: 1.25,
        }}
      >
        {columns.map((c) => {
          const measured = c.value != null;
          const h = measured ? Math.max(4, (Math.max(0, Math.min(1, c.value! / maxValue))) * PLOT_H) : 0;
          return (
            <Box key={c.chip} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 56,
                  height: PLOT_H,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: measured ? 'flex-end' : 'center',
                  alignItems: 'center',
                  gap: 0.75,
                  borderBottom: `1px solid ${VIZ.axis}`,
                }}
              >
                {!measured && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'repeating-linear-gradient(135deg,#F4F4F6 0 6px,#FAFAFB 6px 12px)',
                    }}
                  />
                )}
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    fontSize: measured ? 15 : 12,
                    fontWeight: measured ? 600 : 500,
                    color: measured ? VIZ.ink : '#85858F',
                  }}
                >
                  {measured ? Math.round(c.value!) : unmeasuredNote}
                </Box>
                {measured && (
                  <Box sx={{ width: '100%', height: h, bgcolor: color, borderRadius: '8px 8px 0 0' }} />
                )}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13,
                  fontWeight: 500,
                  bgcolor: '#fff',
                  border: '1px solid #E6E6EA',
                  borderRadius: '6px',
                  px: 1,
                  py: '2px',
                  color: measured ? VIZ.ink : '#85858F',
                }}
              >
                {c.chip}
              </Box>
              <Box component="span" sx={{ fontSize: 12, color: VIZ.muted, minHeight: 18, textAlign: 'center' }}>
                {c.sub ?? ' '}
              </Box>
            </Box>
          );
        })}
      </Box>
      {scaleFrom && scaleTo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 12, color: VIZ.muted }}>
          <span>{scaleFrom}</span>
          <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg,#D9D9DE,${VIZ.violet})` }} />
          <span>{scaleTo}</span>
        </Box>
      )}
    </Box>
  );
}
